import { BASE_PATH, getAccessToken } from "../api/api";
import type {
  FormFieldsPayload,
  FormFillAssignment,
  FormFillResultPayload,
  FormGroupSnapshot,
} from "../embed-shared/protocol";
import { embedRequestHeaders } from "./embedRuntime";

export type FormFillSuggestion = { formKey: string; fieldKey: string; value: string };

export class FormFillBridgeError extends Error {
  constructor(readonly code: "timeout" | "unavailable" | "failed") {
    super(code);
  }
}

type FormFillBridge = {
  requestFields: () => Promise<FormFieldsPayload>;
  applyValues: (values: FormFillAssignment[]) => Promise<FormFillResultPayload>;
};

let bridge: FormFillBridge | null = null;

export function setFormFillBridge(next: FormFillBridge | null): void {
  bridge = next;
}

export function isFormFillEnabled(host: { mode: string; features: { formFill?: boolean } }): boolean {
  return host.mode === "embed" && host.features.formFill === true;
}

/** Keeps suggestions whose key still exists and whose choice value is allowed. */
export function keepKnownSuggestions(
  forms: FormGroupSnapshot[],
  suggestions: FormFillSuggestion[]
): FormFillSuggestion[] {
  const known = new Map<string, FormGroupSnapshot["fields"][number]>();
  for (const form of forms) {
    for (const field of form.fields) known.set(`${form.formKey}\0${field.fieldKey}`, field);
  }
  const used = new Set<string>();
  const out: FormFillSuggestion[] = [];
  for (const suggestion of suggestions) {
    const id = `${suggestion.formKey}\0${suggestion.fieldKey}`;
    const field = known.get(id);
    if (!field || used.has(id)) continue;
    const value = suggestion.value ?? "";
    if (field.control === "checkbox" && value !== "true" && value !== "false") continue;
    if (
      (field.control === "select" || field.control === "radio") &&
      !(field.options || []).some((option) => option.value === value)
    ) {
      continue;
    }
    used.add(id);
    out.push({ formKey: suggestion.formKey, fieldKey: suggestion.fieldKey, value });
  }
  return out;
}

export async function requestHostFormFields(): Promise<FormFieldsPayload> {
  if (!bridge) throw new FormFillBridgeError("unavailable");
  return bridge.requestFields();
}

export async function applyHostFormValues(values: FormFillAssignment[]): Promise<FormFillResultPayload> {
  if (!bridge) throw new FormFillBridgeError("unavailable");
  return bridge.applyValues(values);
}

export async function suggestFormFill(
  answerText: string,
  forms: FormGroupSnapshot[]
): Promise<FormFillSuggestion[]> {
  const token = await getAccessToken();
  if (!token) throw new FormFillBridgeError("failed");
  const res = await fetch(`${BASE_PATH}/api/embed/form-fill/suggest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...embedRequestHeaders(),
    },
    body: JSON.stringify({ answerText, forms }),
  });
  if (!res.ok) throw new FormFillBridgeError("failed");
  const data = (await res.json()) as { suggestions?: unknown };
  if (!Array.isArray(data.suggestions)) return [];
  return data.suggestions.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const row = item as Record<string, unknown>;
    if (typeof row.formKey !== "string" || typeof row.fieldKey !== "string") return [];
    return [{ formKey: row.formKey, fieldKey: row.fieldKey, value: row.value == null ? "" : String(row.value) }];
  });
}
